import React, { useCallback, useState } from 'react';
import { getSudoku } from 'sudoku-gen';

interface Cell {
  value: number | null;
  notes: number[];
  isGiven: boolean;
  hasConflict: boolean;
}

interface SelectedCell {
  row: number;
  col: number;
}

type Grid = Cell[][];

const SudokuApp: React.FC = () => {
  // Initialize empty 9x9 grid with cell data structure
  const initializeGrid = (): Grid => {
    return Array(9).fill(null).map(() => 
      Array(9).fill(null).map(() => ({
        value: null,
        notes: [],
        isGiven: false,
        hasConflict: false
      }))
    );
  };

  const [grid, setGrid] = useState<Grid>(initializeGrid());
  const [selectedCell, setSelectedCell] = useState<SelectedCell>({ row: 0, col: 0 });
  const [isNotesMode, setIsNotesMode] = useState<boolean>(false);

  // Sample puzzle - you can replace this with puzzle generation later
  const loadSamplePuzzle = (): void => {
    const sudoku = getSudoku();

    const newGrid: Grid = initializeGrid();
    for (let i = 0; i < 81; i++) {
      const row = Math.floor(i / 9)
      const col = i % 9; 
        if (sudoku.puzzle.charAt(i) !== '-') {
          newGrid[row][col].value = +sudoku.puzzle.charAt(i);
          newGrid[row][col].isGiven = true;
        }
      }
    setGrid(newGrid);
  };

  // Check for conflicts in row, column, and 3x3 box
  const hasConflict = (grid: Grid, row: number, col: number, value: number | null): boolean => {
    if (!value) return false;

    // Check row
    for (let c = 0; c < 9; c++) {
      if (c !== col && grid[row][c].value === value) return true;
    }

    // Check column
    for (let r = 0; r < 9; r++) {
      if (r !== row && grid[r][col].value === value) return true;
    }

    // Check 3x3 box
    const boxRow: number = Math.floor(row / 3) * 3;
    const boxCol: number = Math.floor(col / 3) * 3;
    for (let r = boxRow; r < boxRow + 3; r++) {
      for (let c = boxCol; c < boxCol + 3; c++) {
        if ((r !== row || c !== col) && grid[r][c].value === value) return true;
      }
    }

    return false;
  };

  // Update conflicts for entire grid
  const updateConflicts = useCallback((newGrid: Grid): void => {
    for (let row = 0; row < 9; row++) {
      for (let col = 0; col < 9; col++) {
        newGrid[row][col].hasConflict = hasConflict(newGrid, row, col, newGrid[row][col].value);
      }
    }
  }, []);

  // Handle number input
  const handleNumberInput = useCallback((number: number): void => {
    const { row, col }: SelectedCell = selectedCell;
    if (grid[row][col].isGiven) return;

    setGrid((prevGrid: Grid) => {
      const newGrid: Grid = prevGrid.map(row => row.map(cell => ({ ...cell })));
      
      if (isNotesMode) {
        // Toggle note
        const notes: number[] = [...newGrid[row][col].notes];
        const noteIndex: number = notes.indexOf(number);
        if (noteIndex > -1) {
          notes.splice(noteIndex, 1);
        } else {
          notes.push(number);
          notes.sort((a, b) => a - b);
        }
        newGrid[row][col].notes = notes;
      } else {
        // Set main value
        newGrid[row][col].value = newGrid[row][col].value === number ? null : number;
        newGrid[row][col].notes = []; // Clear notes when setting main value
      }

      updateConflicts(newGrid);
      return newGrid;
    });
  }, [selectedCell, grid, isNotesMode, updateConflicts]);

  // Handle keyboard input
  const handleKeyDown = useCallback((e: KeyboardEvent): void => {
    const { row, col }: SelectedCell = selectedCell;
    
    // Arrow key navigation
    if (e.key === 'ArrowUp' && row > 0) {
      setSelectedCell({ row: row - 1, col });
    } else if (e.key === 'ArrowDown' && row < 8) {
      setSelectedCell({ row: row + 1, col });
    } else if (e.key === 'ArrowLeft' && col > 0) {
      setSelectedCell({ row, col: col - 1 });
    } else if (e.key === 'ArrowRight' && col < 8) {
      setSelectedCell({ row, col: col + 1 });
    }
    
    // Number input
    const num: number = parseInt(e.key);
    if (num >= 1 && num <= 9) {
      handleNumberInput(num);
    }
    
    // Delete/Backspace
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (!grid[row][col].isGiven) {
        setGrid((prevGrid: Grid) => {
          const newGrid: Grid = prevGrid.map(row => row.map(cell => ({ ...cell })));
          newGrid[row][col].value = null;
          newGrid[row][col].notes = [];
          updateConflicts(newGrid);
          return newGrid;
        });
      }
    }
  }, [selectedCell, handleNumberInput, grid, updateConflicts]);

  // Add event listener for keyboard
  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Render notes in a 3x3 mini-grid
  const renderNotes = (notes: number[]): React.JSX.Element => {
    const noteGrid: (number | null)[] = Array(9).fill(null);
    notes.forEach(note => {
      noteGrid[note - 1] = note;
    });

    return (
      <div 
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: 'repeat(3, 1fr)',
          width: '100%',
          height: '100%',
          fontSize: '12px',
          color: '#6B7280'
        }}
      >
        {noteGrid.map((note: number | null, i: number) => (
          <div 
            key={i} 
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {note || ''}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center p-4 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Sudoku</h1>
      
      {/* Controls */}
      <div className="mb-4 flex gap-4">
        <button
          onClick={loadSamplePuzzle}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Load Sample Puzzle
        </button>
        <button
          onClick={() => setIsNotesMode(!isNotesMode)}
          className={`px-4 py-2 rounded ${
            isNotesMode 
              ? 'bg-green-500 text-white hover:bg-green-600' 
              : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
          }`}
        >
          Notes Mode: {isNotesMode ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Sudoku Grid */}
      <div 
        className="bg-white mb-6 border-4 border-black"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(9, 48px)',
          gridTemplateRows: 'repeat(9, 48px)',
          gap: '0px'
        }}
      >
        {grid.map((row: Cell[], rowIndex: number) =>
          row.map((cell: Cell, colIndex: number) => {
            const isSelected: boolean = selectedCell.row === rowIndex && selectedCell.col === colIndex;
            const isInSameBox: boolean = Math.floor(selectedCell.row / 3) === Math.floor(rowIndex / 3) &&
                               Math.floor(selectedCell.col / 3) === Math.floor(colIndex / 3);
            const isInSameRowOrCol: boolean = selectedCell.row === rowIndex || selectedCell.col === colIndex;
            
            const borderStyle: React.CSSProperties = {
              borderRight: colIndex % 3 === 2 && colIndex < 8 ? '2px solid black' : '1px solid #9CA3AF',
              borderBottom: rowIndex % 3 === 2 && rowIndex < 8 ? '2px solid black' : '1px solid #9CA3AF'
            };

            const backgroundColor = cell.hasConflict ? '#FEE2E2' :
                                  isSelected ? '#DBEAFE' :
                                  isInSameBox ? '#EFF6FF' :
                                  isInSameRowOrCol ? '#F9FAFB' : 'white';

            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                style={{
                  ...borderStyle,
                  backgroundColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: cell.isGiven ? 'bold' : 'normal',
                  color: cell.isGiven ? 'black' : '#2563EB'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = backgroundColor}
                onClick={() => setSelectedCell({ row: rowIndex, col: colIndex })}
              >
                {cell.value ? (
                  <span>{cell.value}</span>
                ) : cell.notes.length > 0 ? (
                  renderNotes(cell.notes)
                ) : null}
              </div>
            );
          })
        )}
      </div>

      {/* Number Input Buttons */}
      <div 
        className="mb-4"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(9, 40px)',
          gap: '8px'
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num: number) => (
          <button
            key={num}
            onClick={() => handleNumberInput(num)}
            style={{
              width: '40px',
              height: '40px',
              backgroundColor: 'white',
              border: '2px solid #D1D5DB',
              borderRadius: '4px',
              fontSize: '18px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            {num}
          </button>
        ))}
      </div>

      {/* Instructions */}
      <div className="text-sm text-gray-600 max-w-md text-center">
        <p className="mb-2">Click a cell to select it, then click a number or use keyboard (1-9).</p>
        <p className="mb-2">Use arrow keys to navigate. Toggle Notes Mode for small numbers.</p>
        <p>Delete/Backspace to clear a cell.</p>
      </div>
    </div>
  );
};

export default SudokuApp;